import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lock } from "lucide-react";
import MultiBrandSelect from "@/components/common/MultiBrandSelect";
import DatePicker from "@/components/common/DatePicker";
import { useAuth } from "@/lib/auth";
import { DEFAULT_OPS_RANGE } from "@/lib/dateDefaults";

/** Reusable filter bar for advanced ops report pages. Emits a `params`
 *  object matching the standard ops API query shape via `onChange` whenever
 *  any control changes. Mirrors the filter row on OpsDashboardPage. */
export default function ReportFilterBar({ onChange, testIdPrefix = "report" }) {
  const { user } = useAuth();
  const isPrivileged = !!(user?.is_admin || user?.is_super_admin);
  const lockedTeamId = !isPrivileged && user?.team_id ? user.team_id : null;
  const _range = DEFAULT_OPS_RANGE();
  const [ctx, setCtx] = useState({ brands: [], teams: [], campaigns: [] });
  const [brandIds, setBrandIds] = useState([]);
  const [teamId, setTeamId] = useState(lockedTeamId || "all");
  const [campaignId, setCampaignId] = useState("all");
  const [dateFrom, setDateFrom] = useState(_range.from);
  const [dateTo, setDateTo] = useState(_range.to);

  useEffect(() => { api.get("/ops/my-context").then((r) => setCtx(r.data)).catch(() => {}); }, []);

  const params = useMemo(() => {
    const p = {};
    if (brandIds.length === 1) p.brand_id = brandIds[0];
    if (teamId !== "all") p.team_id = teamId;
    if (campaignId !== "all") p.campaign_id = campaignId;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  }, [brandIds, teamId, campaignId, dateFrom, dateTo]);

  useEffect(() => { onChange && onChange(params); }, [params, onChange]);

  return (
    <div className="flex gap-3 flex-wrap items-end" data-testid={`${testIdPrefix}-filter-row`}>
      <div className="flex flex-col gap-1">
        <Label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Brand</Label>
        <MultiBrandSelect
          brands={ctx.brands}
          value={brandIds}
          onChange={setBrandIds}
          testId={`${testIdPrefix}-brand-multi`}
        />
      </div>
      {(ctx.teams?.length > 0) && (
        <div className="flex flex-col gap-1">
          <Label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Team</Label>
          {lockedTeamId ? (
            <div className="glass-input h-9 w-[180px] px-3 flex items-center gap-2 text-sm text-slate-600 bg-slate-100/70 cursor-not-allowed">
              <Lock className="w-3.5 h-3.5 text-slate-400" />
              {(ctx.teams.find((t) => t.id === lockedTeamId) || {}).name || "Your team"}
            </div>
          ) : (
            <Select value={teamId} onValueChange={setTeamId}>
              <SelectTrigger className="glass-input h-9 w-[180px]" data-testid={`${testIdPrefix}-team-select`}>
                <SelectValue placeholder="All teams" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All teams</SelectItem>
                {(ctx.teams || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      )}
      {(ctx.campaigns?.length > 0) && (
        <div className="flex flex-col gap-1">
          <Label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Campaign</Label>
          <Select value={campaignId} onValueChange={setCampaignId}>
            <SelectTrigger className="glass-input h-9 w-[200px]" data-testid={`${testIdPrefix}-campaign-select`}>
              <SelectValue placeholder="All campaigns" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All campaigns</SelectItem>
              {(ctx.campaigns || []).map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="flex flex-col gap-1">
        <Label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">From</Label>
        <DatePicker value={dateFrom} onChange={setDateFrom} placeholder="Pick from date"
          className="w-[170px]" testId={`${testIdPrefix}-from`} />
      </div>
      <div className="flex flex-col gap-1">
        <Label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">To</Label>
        <DatePicker value={dateTo} onChange={setDateTo} placeholder="Pick to date"
          className="w-[170px]" testId={`${testIdPrefix}-to`} />
      </div>
    </div>
  );
}
